import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled
} from '@mui/material';
import {
  Tv as TvIcon,
  RestaurantMenu as RestaurantMenuIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import firebaseDataService from '../../services/FirebaseDataService';
import Header from '../layout/Header';
import { mainApi } from '../../config/apiClient';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  backgroundColor: '#f5f5f5',
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  paddingTop: theme.spacing(1),
  overflow: 'auto',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Interfaces
interface QRMenu {
  id: string;
  name: string;
  branchName?: string;
}

const PoleDisplayMenuSelection: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State
  const [menus, setMenus] = useState<QRMenu[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Şirket ID'sini ve kullanıcı rolünü al
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const userData = await firebaseDataService.getUserData(currentUser.uid);
          if (userData) {
            setCompanyId(userData.companyId || '');
            setUserRole(userData.role || '');
          }
        } catch (error) {
          console.error('❌ Kullanıcı bilgileri alınamadı:', error);
          setError('Kullanıcı bilgileri alınamadı.');
        }
      }
    };
    fetchUserData();
  }, [currentUser]);

  // QR Menüleri yükle
  useEffect(() => {
    const fetchMenus = async () => {
      if (!companyId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await mainApi.get(`/api/QRMenu/menu-definitions`);
        
        const result = response.data;
        if (result.success && result.data) {
          const menuList: QRMenu[] = result.data.map((menu: any) => ({
            id: menu.id,
            name: menu.name || 'İsimsiz Menü',
            branchName: menu.branchName
          }));
          
          setMenus(menuList);
        }
      } catch (error) {
        console.error('❌ Menü yükleme hatası:', error);
        setError('Menüler yüklenirken hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    if (companyId && userRole) {
      fetchMenus();
    }
  }, [companyId, userRole, currentUser]);

  // Müşteri ekranına git
  const handleSelectMenu = (menuId: string) => {
    navigate(`/musteri-ekrani/${menuId}`);
  };

  // Geri git
  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/qr-menu');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Header onToggleSidebar={() => {}} isCollapsed={false} />
        <ContentArea>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress size={60} />
          </Box>
        </ContentArea>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header onToggleSidebar={() => {}} isCollapsed={false} />
      
      <ContentArea>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              sx={{ mr: 2 }}
            >
              Geri
            </Button>
            <TvIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Müşteri Ekranı Seçimi
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Müşteri ekranında görüntülemek istediğiniz menüyü seçin
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {menus.length === 0 && !error ? (
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <TvIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Menü Bulunamadı
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Henüz QR menü tanımlanmamış.
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
              <Table size="medium" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '60px' }}>
                      <RestaurantMenuIcon />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '40%' }}>
                      Menü Adı
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '30%' }}>
                      Şube
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', textAlign: 'right', width: '30%' }}>
                      İşlem
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {menus.map((menu) => (
                    <StyledTableRow 
                      key={menu.id}
                      onClick={() => handleSelectMenu(menu.id)}
                    >
                      <TableCell>
                        <TvIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" fontWeight="bold">
                          {menu.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" color="text.secondary">
                          {menu.branchName || 'Şube Atanmamış'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<TvIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectMenu(menu.id);
                          }}
                          sx={{ minWidth: '160px' }}
                        >
                          Müşteri Ekranını Aç
                        </Button>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </ContentArea>
    </PageContainer>
  );
};

export default PoleDisplayMenuSelection;

